import { create } from 'zustand';

/**
 * How many regenerated images are remembered at once. Every tracked id is
 * tested against every rendered src, so the list has to stay short — and an
 * evicted id costs nothing: by then its image exists under the bare URL.
 */
export const MAX_TRACKED_IMAGES = 200;

type ImageCacheBustStore = {
  /**
   * Image id → when the backend last said it finished generating it.
   *
   * A wall-clock timestamp rather than a counter: the browser cache outlives
   * the store, so a version that restarted from 1 on every page load would
   * name a URL the cache already holds — and answer a regenerated image with
   * the one it replaced.
   */
  versions: Record<string, number>;
  bump: (imageId: string) => void;
  reset: () => void;
};

/** Monotonic, so two events for one image in the same tick still differ. */
const nextVersion = (previous?: number): number =>
  Math.max(Date.now(), (previous ?? 0) + 1);

const withoutLeastRecent = (
  versions: Record<string, number>,
): Record<string, number> => {
  const ids = Object.keys(versions);
  if (ids.length <= MAX_TRACKED_IMAGES) return versions;

  // By recency of regeneration, not of first sighting: an image being
  // regenerated over and over is the last one that should stop being tracked.
  const pruned = { ...versions };

  ids
    .sort((a, b) => versions[a] - versions[b])
    .slice(0, ids.length - MAX_TRACKED_IMAGES)
    .forEach((id) => delete pruned[id]);

  return pruned;
};

/**
 * The replacement for polling an image that does not exist yet: the backend
 * says when it does, and the src every component renders changes with it.
 *
 * Lives outside the React tree because the events arrive on a project-wide
 * channel while the images sit in lists, modals and panels that mount and
 * unmount independently of it.
 */
export const useImageCacheBustStore = create<ImageCacheBustStore>((set) => ({
  versions: {},

  bump: (imageId): void =>
    set(({ versions }) =>
      imageId
        ? {
            versions: withoutLeastRecent({
              ...versions,
              [imageId]: nextVersion(versions[imageId]),
            }),
          }
        : { versions },
    ),

  reset: (): void => set({ versions: {} }),
}));

/**
 * Matching is a substring test because the websocket payload names the image
 * id, not the URL: the same id reaches `convertImage` behind every size and
 * resource variant the app renders.
 */
const cacheBust = (src: string, versions: Record<string, number>): string => {
  // Hand-rolled loop rather than `Object.entries().find()`: this runs for every
  // image on screen on every render, and the array that would allocate is pure
  // garbage in the overwhelmingly common case of no match.
  for (const imageId in versions) {
    if (src.includes(imageId))
      return `${src}${src.includes('?') ? '&' : '?'}v=${versions[imageId]}`;
  }

  return src;
};

/**
 * Returns `src` unchanged — same reference, so nothing re-renders — until the
 * image it points at has been regenerated, and a versioned URL from then on.
 */
export const selectCacheBustedSrc =
  (src?: string) =>
  ({ versions }: ImageCacheBustStore): string | undefined =>
    src ? cacheBust(src, versions) : src;

/** [selectCacheBustedSrc](#selectCacheBustedSrc) over a group of images. */
export const selectCacheBustedSrcs =
  (srcs: string[]) =>
  ({ versions }: ImageCacheBustStore): string[] =>
    srcs.map((src) => cacheBust(src, versions));
